import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/StatusPill";
import { Plus, Trash2, UserRound, History, Briefcase } from "lucide-react";
import { toast } from "sonner";

const NONE = "__none__";

export default function Hospedes() {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [form, setForm] = useState({ nome: "", documento: "", telefone: "", email: "", empresa_id: "" });

  const load = async () => {
    try {
      const [{ data: h }, { data: e }] = await Promise.all([api.get("/hospedes"), api.get("/empresas")]);
      setItems(h);
      setEmpresas(e);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar hóspedes. Tente novamente.");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.documento.trim()) return toast.error("Nome e documento são obrigatórios");
    try {
      await api.post("/hospedes", form);
      toast.success("Hóspede cadastrado");
      setOpen(false);
      setForm({ nome: "", documento: "", telefone: "", email: "", empresa_id: "" });
      load();
    } catch {
      toast.error("Erro ao cadastrar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este hóspede?")) return;
    try {
      await api.delete(`/hospedes/${id}`);
      toast.success("Hóspede removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  const filtered = items.filter(
    (h) => !q || h.nome.toLowerCase().includes(q.toLowerCase()) || h.documento.includes(q)
  );

  return (
    <div>
      <PageHeader
        eyebrow="Cadastro"
        title="Hóspedes"
        description="Registro completo de quem se hospeda na rede."
        actions={
          <div className="flex items-center gap-3">
            <Input data-testid="input-search-hospede" placeholder="Buscar por nome ou documento…"
              value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-white" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-hospede" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                  <Plus size={16} className="mr-2" /> Novo hóspede
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">Novo hóspede</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Nome completo</Label>
                    <Input data-testid="input-hospede-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                  </div>
                  <div>
                    <Label>Documento (CPF/RG)</Label>
                    <Input data-testid="input-hospede-documento" value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Telefone</Label>
                      <Input data-testid="input-hospede-telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input data-testid="input-hospede-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Empresa (contratante do quarto — opcional)</Label>
                    <Select
                      value={form.empresa_id || NONE}
                      onValueChange={(v) => setForm({ ...form, empresa_id: v === NONE ? "" : v })}
                    >
                      <SelectTrigger data-testid="select-hospede-empresa">
                        <SelectValue placeholder="Sem empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Sem empresa (pessoa física)</SelectItem>
                        {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="btn-submit-hospede" className="bg-[#083A6B] hover:bg-[#052B50] text-white">Cadastrar</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Documento</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Telefone</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Email</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((h) => (
              <TableRow key={h.id} data-testid={`hospede-row-${h.id}`}>
                <TableCell>
                  <button
                    data-testid={`btn-history-${h.id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/hospedes/${h.id}/reservas`);
                        setHistoryData(data);
                        setHistoryOpen(true);
                      } catch { toast.error("Erro ao carregar histórico"); }
                    }}
                    className="flex items-center gap-3 text-left group"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
                      <UserRound size={14} />
                    </div>
                    <div>
                      <div className="font-medium text-stone-800 group-hover:text-[#083A6B] group-hover:underline decoration-[#D4A652] underline-offset-4">
                        {h.nome}
                      </div>
                      {h.empresa_id && (
                        <div className="text-xs text-[#D4A652] flex items-center gap-1 mt-0.5">
                          <Briefcase size={10} /> {empresas.find((x) => x.id === h.empresa_id)?.nome || ""}
                        </div>
                      )}
                    </div>
                  </button>
                </TableCell>
                <TableCell className="text-stone-600">{h.documento}</TableCell>
                <TableCell className="text-stone-600">{h.telefone || "—"}</TableCell>
                <TableCell className="text-stone-600">{h.email || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button
                    data-testid={`btn-history-icon-${h.id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/hospedes/${h.id}/reservas`);
                        setHistoryData(data);
                        setHistoryOpen(true);
                      } catch { toast.error("Erro ao carregar histórico"); }
                    }}
                    title="Ver histórico"
                    className="text-stone-500 hover:text-[#083A6B] p-1 mr-1"
                  >
                    <History size={16} />
                  </button>
                  <button
                    data-testid={`btn-delete-hospede-${h.id}`}
                    onClick={() => remove(h.id)}
                    className="text-stone-400 hover:text-[#D4A652] p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-stone-500">
                  Nenhum hóspede cadastrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Histórico de estadias
            </DialogTitle>
          </DialogHeader>
          {historyData && (
            <div data-testid="history-content">
              <div className="flex items-start gap-4 mb-6 pb-6 border-b border-stone-200">
                <div className="w-12 h-12 rounded-full bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
                  <UserRound size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-2xl text-stone-900">{historyData.hospede.nome}</div>
                  <div className="text-xs text-stone-500 mt-1">
                    {historyData.hospede.documento} · {historyData.hospede.telefone || "sem telefone"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Estadias</div>
                  <div className="font-serif text-2xl">{historyData.total_estadias}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Total gasto</div>
                  <div className="font-serif text-2xl text-[#083A6B]">{brl(historyData.total_gasto)}</div>
                </div>
              </div>

              {historyData.reservas.length === 0 ? (
                <div className="text-center py-10 text-sm text-stone-500">
                  Este hóspede ainda não possui reservas.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-stone-50 hover:bg-stone-50">
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada / Quarto</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Entrada</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Saída</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Total</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.reservas.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>
                            <div className="font-medium text-stone-800">{r.pousada_nome}</div>
                            <div className="text-xs text-stone-500">Quarto {r.quarto_numero}</div>
                          </TableCell>
                          <TableCell>{fmtDate(r.data_entrada)}</TableCell>
                          <TableCell>{fmtDate(r.data_saida)}</TableCell>
                          <TableCell className="font-medium text-[#083A6B]">{brl(r.valor_total)}</TableCell>
                          <TableCell><StatusPill status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
